import { css } from "@linaria/core";
import * as io from "io-ts";
import cx from "classnames";
import React, { memo } from "react";
import ArrowDownSLine from "@ui/IconV2/react/ArrowDownSLine";
import Text from "@ui/Text";
import { colors, fonts } from "@ui/Theme";
import type { DashboardModelT } from "../../schema/dashboard";
import { DashboardListItem } from "./Item";
import createGlobalState from "@ui/lib/createGlobalState";

const DashboardListDetails = css`
  &:not([open]) summary svg {
    transform: rotateZ(-90deg);
  }

  &[open]:not(:last-child) {
    margin-bottom: 16px;
  }

  summary {
    list-style: none;
    margin-bottom: 8px;

    svg {
      transition: transform 150ms ease-in-out;
    }
  }

  ul {
    padding-left: 8px;

    &:empty::before {
      color: ${colors.carbon500};
      content: attr(data-empty-text);
      font: ${fonts.body1};
    }
  }
`;

const DashboardListHeading = css`
  display: flex;
  gap: 4px;
  align-items: center;
  color: ${colors.carbon700};

  &:not(:first-child) {
    margin-top: 16px;
  }
`;

const useDashboardListToggle = createGlobalState({
  key: "dashboardListToggle",
  storage: "session",
  scoped: true,
  type: io.boolean,
});

export const DashboardList = memo(
  ({
    dashboards,
    selectedDashboardId,
    placeholder,
    title,
    className,
  }: {
    dashboards: DashboardModelT[];
    selectedDashboardId?: string;
    placeholder: string;
    title: string;
    className?: string;
  }) => {
    const [open, setOpen] = useDashboardListToggle({
      defaultValue: true,
      scope: title,
    });
    return (
      <details
        className={cx(DashboardListDetails, className)}
        open={open}
        onClick={e => e.preventDefault()}
      >
        <summary onClick={() => setOpen(!open)}>
          <Text type="body1" as="h3" className={DashboardListHeading}>
            <ArrowDownSLine size="m" />
            {title}
          </Text>
        </summary>
        <ul data-empty-text={placeholder}>
          {dashboards.map(dashboard => (
            <DashboardListItem
              key={dashboard.id}
              dashboard={dashboard}
              selected={dashboard.id === selectedDashboardId}
            />
          ))}
        </ul>
      </details>
    );
  },
);
